import { SALT, ITERATIONS, IV_LENGTH_BYTES, KEY_LENGTH } from '@constants/secret'

/**
 * @license
 * Copyright 2024 Millahue.
 * @typedef {Object} EncryptedObject
 * @property {string} iv - El vector de inicialización (IV) codificado en Base64.
 * @property {string} ciphertext - El texto cifrado codificado en Base64.
 * @description Define la estructura de un objeto que contiene datos encriptados y su vector de inicialización.
 */

// --- Funciones Utilitarias de Criptografía (CryptoUtils) ---

/**
 * @function isWebCryptoAvailable
 * @description Verifica si la Web Crypto API (window.crypto.subtle) está disponible en el entorno actual del navegador.
 * @returns {boolean} True si la Web Crypto API está disponible, false en caso contrario.
 */
export const isWebCryptoAvailable = () => {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
    return true
  }
  if (typeof window !== 'undefined') {
    // Solo muestra la advertencia en el navegador
    console.warn('Web Crypto API no está disponible en este navegador.')
  }
  return false
}

/**
 * @function arrayBufferToBase64
 * @description Convierte un ArrayBuffer a una cadena Base64 de forma asíncrona utilizando la API FileReader.
 * Es una alternativa moderna a `btoa` para datos binarios.
 * @async
 * @param {ArrayBuffer} buffer - El ArrayBuffer a convertir.
 * Este buffer puede ser un Uint8Array o cualquier otro tipo de ArrayBuffer.
 * @param {BlobPropertyBag} [options] - Opciones para crear el Blob. Por defecto, se usa 'application/octet-stream'.
 * @param {string} [options.type='application/octet-stream'] - El tipo MIME del Blob. Por defecto, 'application/octet-stream'.
 * @param {string} [options.endings='native'] - Especifica cómo manejar los finales de línea. Por defecto, 'native'.
 * @param {string} [options.encoding='utf-8'] - Especifica la codificación del texto. Por defecto, 'utf-8'.
 * @param {string} [options.fileName] - Nombre del archivo para el Blob. No afecta la conversión a Base64.
 * @param {string} [options.lastModified] - Fecha de última modificación del Blob. No afecta la conversión a Base64.
 * @param {string} [options.lastModifiedDate] - Fecha de última modificación del Blob. No afecta la conversión a Base64.
 * @param {string} [options.size] - Tamaño del Blob. No afecta la conversión a Base64.
 * @param {string} [options.type] - Tipo MIME del Blob. Por defecto, 'application/octet-stream'.
 * @param {string} [options.endings] - Especifica cómo manejar los finales de línea. Por defecto, 'native'.
 * @param {string} [options.encoding] - Especifica la codificación del texto. Por defecto, 'utf-8'.
 * @param {string} [options.fileName] - Nombre del archivo para el Blob. No afecta la conversión a Base64.
 * @param {string} [options.lastModified] - Fecha de última modificación del Blob. No afecta la conversión a Base64.
 * @param {string} [options.lastModifiedDate] - Fecha de última modificación del Blob. No afecta la conversión a Base64.
 * @returns {Promise<string>} Una promesa que se resuelve con la representación en Base64 del buffer.
 * @throws {Error} Si ocurre un error durante la conversión.
 * @example
 * // Ejemplo de uso:
 *   // const buffer = new Uint8Array([72, 101, 108, 108, 111]).buffer; // "Hello" en ArrayBuffer
 *   // const base64String = await arrayBufferToBase64(buffer);
 *   // console.log(base64String); // "SGVsbG8="
 */
export const arrayBufferToBase64 = buffer => {
  return new Promise((resolve, reject) => {
    const blob = new Blob([buffer], { type: 'application/octet-stream' })
    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = reader.result
      const base64String = dataUrl.substring(dataUrl.indexOf(',') + 1)
      resolve(base64String)
    }
    reader.onerror = error => {
      console.error('Error convirtiendo ArrayBuffer a Base64:', error)
      reject(new Error('Error al convertir ArrayBuffer a Base64.'))
    }
    reader.readAsDataURL(blob)
  })
}

/**
 * @function base64ToUint8Array
 * @description Convierte una cadena Base64 a un Uint8Array de forma asíncrona utilizando la API fetch con una URL de datos.
 * Es una alternativa moderna a `atob`.
 * @async
 * @param {string} base64 - La cadena Base64 a convertir.
 * @returns {Promise<Uint8Array>} Una promesa que se resuelve con el Uint8Array resultante.
 * @throws {Error} Si ocurre un error durante la conversión.
 * @example
 * // Ejemplo de uso:
 * // const base64String = 'SGVsbG8sIFdvcmxkIQ=='; // "Hello, World!" en Base64
 * // const uint8Array = await base64ToUint8Array(base64String);
 * // console.log(uint8Array); // Uint8Array con los bytes correspondientes
 * // console.log(new TextDecoder().decode(uint8Array)); // "Hello, World!"
 */
export const base64ToUint8Array = async base64 => {
  // Input validation: Ensure base64 is a string.
  // This is a basic security and robustness measure.
  if (typeof base64 !== 'string') {
    const errorMessage = 'Entrada inválida: el parámetro base64 debe ser una cadena de texto.'
    console.error(errorMessage) // Log the specific error message
    throw new TypeError(errorMessage)
  }

  try {
    // The fetch API is used here to decode Base64.
    // For data URLs, 'application/octet-stream' MIME type is important
    // as it instructs the browser to treat the decoded data as raw binary,
    // mitigating risks if the Base64 decoded to, for example, executable script content
    // and a different MIME type was used.
    const response = await fetch(`data:application/octet-stream;base64,${base64}`, {
      // HTTP headers are for network requests and generally don't apply to 'data:' URL
      // processing. The 'application/octet-stream' MIME type in the URL is key for security.
      headers: {
        'X-Content-Type-Options': 'nosniff', // Security header; non-operational for data: URLs.
        'Referrer-Policy': 'no-referrer' // Security header; non-operational for data: URLs.
      }
    })

    // Crucial check: ensure the fetch response is 'ok' (e.g., status 200-299).
    // For data URLs, if fetch() itself doesn't throw (e.g., due to URL length limits
    // or a fundamentally malformed data URL structure), the status is typically 200.
    // This check handles unexpected cases where the response might not be 'ok'.
    if (!response.ok) {
      const errorText = `Error al procesar la data URL con Base64: Respuesta no OK (estado ${response.status} ${response.statusText})`
      console.error(errorText) // Log the specific error message
      throw new Error(errorText) // Throw an error with details from the response.
    }

    const arrayBuffer = await response.arrayBuffer()
    // If the base64 string was malformed (e.g., invalid characters),
    // response.arrayBuffer() might throw an error, or the browser might attempt
    // a partial decoding. Such errors will be caught by the catch block.
    return new Uint8Array(arrayBuffer)
  } catch (error) {
    // Log the full error object for debugging, consistent with the original code's logging style.
    console.error('Error convirtiendo Base64 a Uint8Array:', error)

    // Re-throw the error.
    // If it's one of our custom errors (TypeError from input validation, or the Error from !response.ok),
    // re-throw it as is, as it's already descriptive.
    if (
      error instanceof TypeError ||
      (error.message && error.message.startsWith('Error al procesar la data URL con Base64:'))
    ) {
      throw error
    }

    // For other errors (e.g., from fetch() itself failing due to an invalid data URL structure
    // not caught earlier, or from .arrayBuffer() failing due to malformed Base64 content):
    // Wrap the original error in a new Error to provide context, including the original error's message.
    // This improves upon the original generic error message.
    throw new Error(`Conversión de Base64 a Uint8Array fallida. Causa original: ${error.message}`)
  }
}

/**
 * @function getEncryptionKey
 * @description Deriva una `CryptoKey` para AES-GCM a partir de una frase de contraseña humana
 * utilizando el algoritmo PBKDF2 (Password-Based Key Derivation Function 2).
 * Esta función es fundamental para transformar una contraseña memorable en una clave criptográfica segura.
 * @async
 * @param {string} passphrase - La frase de contraseña proporcionada por el usuario o la aplicación.
 * @param {Uint8Array} salt - Un valor de "salt" criptográfico. Debe ser único y consistente para una frase de contraseña dada
 * para asegurar que se derive la misma clave. No necesita ser secreto.
 * @param {number} iterations - El número de iteraciones que PBKDF2 realizará. Un número mayor
 * aumenta la seguridad contra ataques de fuerza bruta, pero también el tiempo de derivación.
 * @returns {Promise<CryptoKey>} Una promesa que se resuelve con el objeto `CryptoKey` derivado,
 * listo para ser usado en operaciones de cifrado y descifrado AES-GCM.
 * @throws {Error} Si la Web Crypto API no está disponible (aunque se espera que `isWebCryptoAvailable` lo verifique antes)
 * o si ocurre algún error durante el proceso de importación o derivación de la clave.
 */
export const getEncryptionKey = async (passphrase, salt, iterations) => {
  const passphraseBuffer = new TextEncoder().encode(passphrase)
  // Importa la frase de contraseña como una clave base para PBKDF2.
  const importedKey = await window.crypto.subtle.importKey('raw', passphraseBuffer, { name: 'PBKDF2' }, false, [
    'deriveKey'
  ])
  // Deriva la clave final usando PBKDF2 con los parámetros especificados.
  return window.crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations, hash: 'SHA-256' }, // Algoritmo y parámetros de derivación
    importedKey, // La clave base importada
    { name: 'AES-GCM', length: KEY_LENGTH }, // Algoritmo y longitud de la clave deseada
    true, // `extractable` se establece en true para permitir su uso posterior. Considera false si no se exportará.
    ['encrypt', 'decrypt'] // Usos permitidos para la clave derivada.
  )
}

/**
 * @function encryptData
 * @description Cifra datos en texto plano utilizando el algoritmo AES-GCM.
 * Primero, deriva una clave de cifrado a partir de la frase de contraseña proporcionada.
 * Luego, genera un nuevo vector de inicialización (IV) único para esta operación de cifrado.
 * El IV es esencial para la seguridad de AES-GCM y debe almacenarse junto con el texto cifrado.
 * @async
 * @param {any} data - Los datos en texto plano que se van a cifrar. Deben ser serializables a JSON.
 * @param {string} passphrase - La frase de contraseña utilizada para derivar la clave de cifrado.
 * @returns {Promise<EncryptedObject|null>} Una promesa que se resuelve con un objeto `EncryptedObject`
 * (conteniendo `iv` y `ciphertext` en Base64), o `null` si
 * ocurre un error durante el proceso de cifrado.
 */
export const encryptData = async (data, passphrase) => {
  try {
    const key = await getEncryptionKey(passphrase, SALT, ITERATIONS)
    const iv = window.crypto.getRandomValues(new Uint8Array(IV_LENGTH_BYTES)) // IV fresco y aleatorio
    const stringifiedValue = JSON.stringify(data) // Los datos deben ser un string para TextEncoder
    const encodedData = new TextEncoder().encode(stringifiedValue)

    const ciphertextBuffer = await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encodedData)

    // Convierte el IV y el texto cifrado (ambos ArrayBuffers) a Base64 para un almacenamiento más fácil.
    const ivBase64 = await arrayBufferToBase64(iv.buffer) // iv es Uint8Array, su buffer es ArrayBuffer
    const ciphertextBase64 = await arrayBufferToBase64(ciphertextBuffer)

    return { iv: ivBase64, ciphertext: ciphertextBase64 }
  } catch (error) {
    console.error('Error encriptando datos:', error)
    return null
  }
}

/**
 * @function decryptData
 * @description Descifra datos que fueron previamente cifrados utilizando AES-GCM.
 * Requiere el objeto `EncryptedObject` (que contiene el IV y el texto cifrado) y la misma
 * frase de contraseña que se usó para cifrar.
 * @async
 * @param {EncryptedObject} encryptedObject - El objeto que contiene `iv` (Base64) y `ciphertext` (Base64).
 * @param {string} passphrase - La frase de contraseña utilizada para derivar la clave de descifrado.
 * @returns {Promise<any|null>} Una promesa que se resuelve con los datos originales descifrados y parseados (desde JSON),
 * o `null` si ocurre un error (ej., frase de contraseña incorrecta, datos corruptos,
 * formato de `encryptedObject` inválido).
 */
export const decryptData = async (encryptedObject, passphrase) => {
  try {
    if (!encryptedObject || typeof encryptedObject.iv !== 'string' || typeof encryptedObject.ciphertext !== 'string') {
      console.error('Objeto encriptado inválido proporcionado para desencriptación.')
      return null
    }
    const key = await getEncryptionKey(passphrase, SALT, ITERATIONS)
    // Convierte el IV y el texto cifrado de Base64 de nuevo a datos binarios (Uint8Array/ArrayBuffer).
    const iv = await base64ToUint8Array(encryptedObject.iv)
    const ciphertextBytes = await base64ToUint8Array(encryptedObject.ciphertext)
    const ciphertext = ciphertextBytes.buffer // .buffer para obtener el ArrayBuffer subyacente

    const decryptedBuffer = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      ciphertext // El IV debe ser el mismo usado para encriptar
    )

    const decryptedString = new TextDecoder().decode(decryptedBuffer)
    return JSON.parse(decryptedString) // Parsea el string JSON de vuelta al objeto original.
  } catch (error) {
    // Los errores aquí son comunes si la frase de contraseña es incorrecta o los datos están corruptos.
    if (process.env.NODE_ENV !== 'production') {
      console.error(
        'Error desencriptando datos (verifique la frase de contraseña y la integridad de los datos):',
        error,
        JSON.stringify(encryptedObject, null, 2) // Muestra el objeto encriptado para depuración
      )
    }

    return null
  }
}
